import { useState, useMemo, useEffect, type ChangeEvent } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import TabNavigation from "@app/components/layout/TabNavigation";
import TabPanels from "@app/components/layout/TabPanels";
import ZakyamLogo from "@app/components/layout/ZakyamLogo";
import { dashboardTabs } from "@app/config/tabs";
import { useUiStore, type UiState } from "@app/state/uiStore";
import { ProfilePanel } from "@app/components/layout/ProfilePanel";
import { useAppConfig } from "@app/hooks/useAppConfig";
import { filterTabsByAllowed } from "@app/utils/tabFiltering";
import { useAuth } from "@app/hooks/useAuth";
import { LoginPage } from "@app/components/auth/LoginPage";
import { CryptoTicker } from "@app/components/ticker/CryptoTicker";
import PublicApp from "./PublicApp";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60000,
      gcTime: 300000,
      retry: 3,
      refetchOnWindowFocus: true,
    },
  },
});

import { isPublicRoute } from "./constants/publicRoutes";

function App() {
  // Check if this is a public route FIRST (before any hooks)
  // This prevents unnecessary API calls for public pages
  const pathname = window.location.pathname;
  const isPublic = isPublicRoute(pathname);

  // Show public pages without authentication (no hooks called)
  if (isPublic) {
    return (
      <QueryClientProvider client={queryClient}>
        <PublicApp />
      </QueryClientProvider>
    );
  }

  // Only call hooks for authenticated pages
  const activeTab = useUiStore((store: UiState) => store.activeTab);
  const [language, setLanguage] = useState("en");
  const [isProfileOpen, setProfileOpen] = useState(false);
  
  // CRITICAL FIX: Check for login_success flag in URL
  // If present, skip showing LoginPage even if isAuthenticated is false initially
  const urlParams = new URLSearchParams(window.location.search);
  const loginSuccess = urlParams.get("login_success") === "true";
  
  // Check authentication
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  
  // CRITICAL FIX: Clean up login_success parameter from URL after processing
  // Also clear logout_flag when user successfully logs in
  useEffect(() => {
    if (loginSuccess) {
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);
      // Clear logout_flag on successful login to allow normal authentication
      localStorage.removeItem("logout_flag");
    }
  }, [loginSuccess]);

  // Fetch app config to get allowed_tabs
  const { data: appConfig, isLoading: configLoading } = useAppConfig();
  
  const displayName =
    user?.name && user.name !== user.email
      ? user.name
      : user?.email?.split("@")[0] || "User";
  const avatarUrl = user?.picture || "";

  // Filter tabs based on allowed_tabs from backend
  const visibleTabs = useMemo(() => {
    if (configLoading || !appConfig) {
      // While loading, show all tabs (will be filtered once config loads)
      return dashboardTabs;
    }
    return filterTabsByAllowed(dashboardTabs, appConfig.allowed_tabs);
  }, [appConfig, configLoading]);

  // Guard: Redirect to dashboard if user navigates directly to non-allowed route
  const allowedTabIds = useMemo(() => {
    if (!appConfig || !appConfig.allowed_tabs) {
      return new Set<string>();
    }
    const filtered = filterTabsByAllowed(dashboardTabs, appConfig.allowed_tabs);
    return new Set(filtered.map((tab) => tab.id));
  }, [appConfig]);

  // Handle ?tab= query parameter on initial load (e.g. ?tab=integrations after subscription)
  useEffect(() => {
    if (configLoading) return;
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get("tab");
    if (tabParam && allowedTabIds.has(tabParam) && dashboardTabs.some((t) => t.id === tabParam)) {
      const { setActiveTab } = useUiStore.getState();
      setActiveTab(tabParam);
      // Clean URL without reloading the page
      const url = new URL(window.location.href);
      url.searchParams.delete("tab");
      window.history.replaceState({}, "", url.toString());
    }
  }, [configLoading, allowedTabIds]);

  // Check if activeTab is allowed, redirect to first allowed tab if not
  useEffect(() => {
    if (!configLoading && appConfig && activeTab && !allowedTabIds.has(activeTab)) {
      const firstAllowedTab = visibleTabs.length > 0 ? visibleTabs[0].id : "overview";
      const { setActiveTab } = useUiStore.getState();
      setActiveTab(firstAllowedTab);
    }
  }, [activeTab, allowedTabIds, configLoading, appConfig, visibleTabs]);

  // Redirect new users to Integrations tab if they have no trading accounts set up
  // Only applies to non-admin users with an active subscription
  useEffect(() => {
    if (configLoading || !appConfig) return;
    if (activeTab === "integrations") return;
    const isAdmin = appConfig.role === "admin";
    const hasActiveSubscription = appConfig.plan_code !== null && appConfig.plan_code !== undefined;
    if (!isAdmin && hasActiveSubscription && !appConfig.has_integrations) {
      const { setActiveTab } = useUiStore.getState();
      setActiveTab("integrations");
    }
  }, [configLoading, appConfig, activeTab]);

  const handleLanguageChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setLanguage(event.target.value);
  };

  const toggleProfile = () => {
    setProfileOpen((prev) => !prev);
  };

  // Show login page if not authenticated
  // CRITICAL FIX: If login_success flag is present, show loading instead of LoginPage
  // This prevents showing landing page during redirect after successful login
  if (authLoading || (loginSuccess && !isAuthenticated)) {
    return (
      <QueryClientProvider client={queryClient}>
        <div className="app-shell" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
          <div>Loading...</div>
        </div>
      </QueryClientProvider>
    );
  }

  if (!isAuthenticated) {
    return (
      <QueryClientProvider client={queryClient}>
        <LoginPage />
      </QueryClientProvider>
    );
  }

  // Frontend guard: Redirect to /subscribe if no active subscription (UX only, not security)
  // Admins bypass this check
  if (!configLoading && appConfig) {
    const isAdmin = appConfig.role === "admin";
    const hasActiveSubscription = appConfig.plan_code !== null && appConfig.plan_code !== undefined;
    
    if (!isAdmin && !hasActiveSubscription) {
      // Redirect to subscribe page
      window.location.href = "/subscribe";
      return (
        <QueryClientProvider client={queryClient}>
          <div className="app-shell" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
            <div>Redirecting to subscription page...</div>
          </div>
        </QueryClientProvider>
      );
    }
  }

  return (
    <QueryClientProvider client={queryClient}>
    <div className="app-shell">
      <CryptoTicker />
      <header className="app-header">
        <div className="app-header__top">
          <div className="app-brand">
            <ZakyamLogo className="app-brand__logo" />
          </div>
          <div className="app-header__actions">
            <div className="language-switcher">
              <span aria-hidden="true" className="language-switcher__icon">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="2" y1="12" x2="22" y2="12" />
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
              </span>
              <select value={language} onChange={handleLanguageChange} aria-label="Change interface language">
                <option value="en">English</option>
                <option value="ar">Arabic</option>
              </select>
            </div>
            <button
              type="button"
              className="profile-trigger"
              onClick={toggleProfile}
              aria-haspopup="dialog"
              aria-label="Open profile settings"
            >
              <span className="profile-trigger__avatar" aria-hidden="true">
                {avatarUrl ? (
                  <img src={avatarUrl} alt={`${displayName} avatar`} />
                ) : (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                )}
              </span>
              <span className="profile-trigger__name">{displayName}</span>
              <span className="profile-trigger__caret" aria-hidden="true">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </button>
          </div>
        </div>
        <p>Autonomous AI execution, news intelligence, and capital oversight under one command center.</p>
      </header>
      <nav className="tabs-container">
        <TabNavigation tabs={visibleTabs} activeTab={activeTab} grouped={appConfig?.role === "admin"} />
      </nav>
      <main>
        <TabPanels tabs={visibleTabs} activeTab={activeTab} />
      </main>
      <ProfilePanel isOpen={isProfileOpen} onClose={toggleProfile} language={language} />
    </div>
    </QueryClientProvider>
  );
}

export default App;

